import Box from '@risk-smart/themed-cloudscape-components/box';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import type { IndicatorBand } from '@risksmart-app/domain/src/types/indicator-banding-config';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import type { FC, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { useLineChartOptions } from '../../../../../components/highcharts/line-chart/useLineChartOptions';
import { RSHighcharts } from '../../../../../components/highcharts/RSHighcharts';
import {
  buildIndicatorReferenceLines,
  buildIndicatorReferenceLinesFromConfig,
} from '../../../indicatorReferenceLines';
import type { ResolvedDateRange } from './dateRange';
import { filterResultsByDateRange, resolveChartXDomain } from './dateRange';

type Props = {
  data: { ResultDate: string; TargetValueNum?: null | number }[];
  dateRange?: ResolvedDateRange;
  upperTolerance?: null | number;
  lowerTolerance?: null | number;
  upperAppetite?: null | number;
  lowerAppetite?: null | number;
  // CUS-188 `configurable_thresholds`: when on, reference lines come from the
  // indicator's CURRENT banding config boundaries instead of the legacy
  // tolerance/appetite props above. This only affects the chart's reference
  // lines (boundary lines, not zone fills — M3) — it never re-evaluates or
  // repaints already-plotted result points, which stay sourced from `data`.
  isConfigurableThresholdsEnabled?: boolean;
  bands?: IndicatorBand[];
  /** Exposes the Highcharts instance for the Actions dropdown's PNG export (CUS-145). */
  ref?: Ref<HighchartsReactRefObject>;
};

const CHART_HEIGHT = 300;

function numberFormatter(e: number) {
  return Math.abs(e) >= 1e9
    ? (e / 1e9).toFixed(1).replace(/\.0$/, '') + 'G'
    : Math.abs(e) >= 1e6
      ? (e / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'
      : Math.abs(e) >= 1e3
        ? (e / 1e3).toFixed(1).replace(/\.0$/, '') + 'K'
        : e.toFixed(2);
}

const getSeriesData = (
  data: { ResultDate: string; TargetValueNum?: null | number }[],
  dateRange?: ResolvedDateRange
) => {
  return filterResultsByDateRange(data, dateRange)
    .map((d) => ({
      x: new Date(d.ResultDate),
      y: d.TargetValueNum ?? 0,
    }))
    .sort((a, b) => a.x.getTime() - b.x.getTime());
};

const ResultsChart: FC<Props> = ({
  data,
  dateRange,
  upperTolerance,
  lowerTolerance,
  lowerAppetite,
  upperAppetite,
  isConfigurableThresholdsEnabled = false,
  bands,
  ref,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'indicator_results' });
  const { getColorClass, getLabel } = useRating('indicator_conformance_status');

  const referenceLines = isConfigurableThresholdsEnabled
    ? buildIndicatorReferenceLinesFromConfig(bands ?? [])
    : buildIndicatorReferenceLines(
        { upperTolerance, lowerTolerance, upperAppetite, lowerAppetite },
        { getColorClass, getLabel }
      );

  const seriesData = getSeriesData(data, dateRange);
  const showEmptyState = Boolean(dateRange) && seriesData.length === 0;

  const options = {
    ...useLineChartOptions({
      xTitle: 'Date',
      yTitle: 'Result',
      colours: [getColorStyles('charts-teal-300').backgroundColor],
      series: [{ name: 'Result', data: seriesData }],
      referenceLines,
      isReferenceLineLabelsShown: true,
      yLabelFormatter: numberFormatter,
      hasYDecimals: true,
      tooltipYValueFormatter: (y) => numberFormatter(y ?? 0),
      isLegendShown: false,
      xDomain: resolveChartXDomain(dateRange),
      height: CHART_HEIGHT,
    }),
    // The exporting module is loaded globally (RSHighcharts), which would add
    // Highcharts' default in-chart context button — the Cloudscape chart had no
    // such control, and export must only be offered through the
    // permission-gated Actions dropdown (CUS-145).
    exporting: {
      buttons: { contextButton: { enabled: false } },
      // Exports render fully client-side (offline-exporting is loaded by
      // RSHighcharts) — never post chart data to Highcharts' export server.
      fallbackToExportServer: false,
    },
  };

  // Surface the empty state (no results at all, or none within the applied
  // range) instead of an axes-only chart. Only with a date range applied — the
  // feature-flag-off path keeps the bare chart, matching the previous
  // behaviour.
  if (showEmptyState) {
    return (
      <div
        className={'flex items-center justify-center'}
        style={{ height: CHART_HEIGHT }}
      >
        <Box textAlign={'center'}>{t('date_range.empty_chart')}</Box>
      </div>
    );
  }

  return (
    <RSHighcharts
      ref={ref}
      options={options}
      containerProps={{ style: { height: `${CHART_HEIGHT}px` } }}
    />
  );
};

export default ResultsChart;
